import React, { ComponentType, useEffect, useMemo, useRef, useState } from 'react'
import classNames from 'classnames'
import { AiResult } from '../../../../../../models/AiResult'
import { MeetingTemplatePresetView } from '../../../../../../views/Setup/MeetingTemplates/MeetingTemplateView'
import { ResultSelector } from './ResultSelector'
import { ResultDisplay } from './Body/ResultDisplay'
import { CollapsedResultDisplay } from './Body/CollapsedResultDisplay'
import { Collapse } from 'react-bootstrap'
import { ManuallyRunnableAiFeature } from './Body/ManuallyRunnableAiFeature'
import { UninstalledAiFeature } from './Body/UninstalledAiFeature'
import { NoAiFeatureFound } from './Body/NoAiFeatureFound'
import { LoadingResult } from './Body/LoadingResult'
import { ResultCardHeader } from './Header/ResultCardHeader'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../../../../../store/hooks'
import { selectAiFeatures } from '../../../../../../store/meetingSummary/aiFeatures'
import { selectAiFeaturePresets } from '../../../../../../store/meetingSummary/aiFeaturePresets'
import { selectExecutedAiFeatures, selectNonExecutedPresetsForTemplate } from '../../../../../../store/meetingSummary/executedAiFeatures'
import { selectAiResults, selectSavedResultId, setSavedResultId } from '../../../../../../store/meetingSummary/aiResults'
import { selectSelectedMeetingTemplate, setSelectedMeetingTemplate } from '../../../../../../store/meetingSummary/meetingTemplates'
import { selectInputParametersHierarchyForSelectedMeetingTemplate } from '../../../../../../store/meetingSummary/inputParametersHierarchy'
import { getPresetResultsByInputParameterHierarchy } from '../../utils'
import './ResultCard.scss'
import { HeaderResultOption } from './Header/HeaderResultOptions/HeaderResultOptionsButton'
import { AiFeature } from '../../../../../../models/AiFeature'
import { HeaderResultOptionConfig } from './Header/HeaderResultOptions/DefaultHeaderResultOptions'

interface ResultCardProps {
  className?: string
  meetingTemplatePresetView: MeetingTemplatePresetView
  row?: number
  shouldOpenAll?: boolean
  extraHeaderOptions?: HeaderResultOption[]
  headerResultOptionsConfig?: Partial<HeaderResultOptionConfig>
  showEditButtons?: boolean
  plugins?: ComponentType<ResultCardPluginProps>[]
}

export interface ResultCardPluginProps {
  aiFeature?: AiFeature
  aiResult?: AiResult
}

export const ResultCard: React.FC<ResultCardProps> = ({
  className,
  meetingTemplatePresetView,
  row = 0,
  shouldOpenAll = false,
  extraHeaderOptions,
  headerResultOptionsConfig,
  showEditButtons = true,
  plugins,
}) => {
  const dispatch = useAppDispatch()

  const { hash } = useLocation()
  const navigate = useNavigate()

  const { aiFeaturePresetId, aiFeatureId } = meetingTemplatePresetView

  const aiFeatures = useAppSelector(selectAiFeatures)
  const aiFeaturePresets = useAppSelector(selectAiFeaturePresets)
  const executedAiFeatures = useAppSelector(selectExecutedAiFeatures)
  const allAiResults = useAppSelector(selectAiResults)
  const inputParametersHierarchy = useAppSelector(selectInputParametersHierarchyForSelectedMeetingTemplate)
  const selectedMeetingTemplate = useAppSelector(selectSelectedMeetingTemplate)
  const savedResultId = useAppSelector(selectSavedResultId)
  const nonExecutedPresets = useAppSelector(selectNonExecutedPresetsForTemplate)

  const aiFeaturePreset = aiFeaturePresets.find(({ id }) => id === aiFeaturePresetId)
  const aiFeature = aiFeatures.find(({ id }) => id === aiFeatureId)

  const aiResults = useMemo(() => {
    if (!aiFeature || !aiFeaturePreset || !inputParametersHierarchy) {
      return []
    }
    const { inputParametersHierarchyNodeByPresets } = inputParametersHierarchy
    const presetAiResults = getPresetResultsByInputParameterHierarchy(aiFeaturePreset.id, aiFeature.id, allAiResults, inputParametersHierarchyNodeByPresets)
    const presetAiResultsWithoutNoResult = presetAiResults.filter((r) => r.status !== 'no_result')
    return presetAiResultsWithoutNoResult.length ? presetAiResultsWithoutNoResult : presetAiResults
  }, [aiFeature, aiFeaturePreset, inputParametersHierarchy, allAiResults])

  const [selectedResult, setSelectedResult] = useState<AiResult>()

  const name = aiFeature?.name ?? aiFeaturePreset?.name ?? ''

  const aiFeatureWasExecutedWithInputParameter =
    aiFeature &&
    aiFeaturePreset &&
    inputParametersHierarchy &&
    executedAiFeatures.some((executedFeature) => {
      const { id: executedAiFeatureId, presetId: executedAiFeaturePresetId, inputParametersHierarchyId: executedInputParameterId = null } = executedFeature
      const inputParameterHierarchyIdForPreset = inputParametersHierarchy.inputParametersHierarchyNodeByPresets[aiFeaturePreset.id]?.id ?? null
      return (
        executedAiFeatureId === aiFeature.id &&
        executedAiFeaturePresetId === aiFeaturePreset.id &&
        executedInputParameterId === inputParameterHierarchyIdForPreset
      )
    })

  const noResults = !aiFeatureId || !aiFeature || (!aiFeatureWasExecutedWithInputParameter && !selectedResult)

  const [collapsed, setCollapsed] = useState<boolean>(true)

  const cardRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!aiResults.length) {
      return
    }

    if (!selectedResult) {
      setSelectedResult(aiResults[0])
    } else if (savedResultId && aiResults.some((r) => r.id === savedResultId)) {
      setSelectedResult(aiResults.find((r) => r.id === savedResultId) ?? aiResults[0])
      dispatch(setSavedResultId(null))
    } else if (!aiResults.includes(selectedResult)) {
      setSelectedResult(aiResults[0])
    }
  }, [dispatch, selectedResult, aiResults, savedResultId])

  useEffect(() => {
    setCollapsed(!noResults && row !== 0)
  }, [meetingTemplatePresetView, row, noResults])

  useEffect(() => {
    if (shouldOpenAll) {
      setCollapsed(!shouldOpenAll)
    }
  }, [shouldOpenAll])

  useEffect(() => {
    if (!selectedMeetingTemplate) {
      return
    }
    if (hash && hash.startsWith('#')) {
      const [linkedAiFeatureId, meetingTemplateId] = hash.substring(1).split('-')
      if (!linkedAiFeatureId) {
        return
      }
      if (aiFeatureId !== linkedAiFeatureId) {
        setCollapsed(true)
        return
      }
      if (meetingTemplateId && meetingTemplateId !== selectedMeetingTemplate.id) {
        dispatch(setSelectedMeetingTemplate(meetingTemplateId))
      } else {
        navigate('#')
      }
      setCollapsed(false)
      cardRef.current?.scroll({ behavior: 'smooth' })
    }
  }, [dispatch, hash, navigate, aiFeatureId, selectedMeetingTemplate])

  const getResultCardBody = () => {
    if (!aiFeatureId) {
      return <UninstalledAiFeature />
    }

    if (!aiFeature) {
      return <NoAiFeatureFound />
    }

    if (!executedAiFeatures.length) {
      return <LoadingResult />
    }

    if (nonExecutedPresets.some((preset) => preset.aiFeatureId === aiFeature.id)) {
      return <ManuallyRunnableAiFeature aiFeature={aiFeature} />
    }

    if (!selectedResult) {
      return <LoadingResult />
    }

    return (
      <ResultDisplay
        aiFeature={aiFeature}
        aiResult={selectedResult}
        row={row}
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        showEditButtons={showEditButtons}
      />
    )
  }

  const getCollapsedResultCardBody = () => {
    if (noResults) {
      return null
    }

    if (!selectedResult) {
      return <LoadingResult />
    }

    return <CollapsedResultDisplay aiFeature={aiFeature} aiResult={selectedResult} row={row} collapsed={collapsed} setCollapsed={setCollapsed} />
  }

  const handleResultSelected = (newSelectedResult: AiResult) => {
    setSelectedResult(newSelectedResult)
  }

  return (
    <div
      ref={cardRef}
      className={classNames('result-card-container d-flex flex-column', {
        'py-3': collapsed,
        [className ?? 'pt-3 pb-5']: !collapsed,
      })}
    >
      <ResultCardHeader
        presetId={aiFeaturePresetId}
        name={name}
        aiFeature={aiFeature}
        aiResult={selectedResult}
        noResults={noResults}
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        headerResultOptionsConfig={headerResultOptionsConfig}
        extraHeaderOptions={extraHeaderOptions}
      />
      {collapsed && getCollapsedResultCardBody()}
      <Collapse in={!collapsed}>
        <div>
          <div className={classNames('d-flex flex-column pt-2 px-4 gap-2')}>
            {!collapsed && aiResults.length > 1 && aiFeature ? (
              <ResultSelector aiFeature={aiFeature} aiResults={aiResults} onResultSelected={handleResultSelected} />
            ) : null}
            {getResultCardBody()}
          </div>
        </div>
      </Collapse>
      {plugins
        ? plugins.map((ResultCardPlugin, idx) => {
            return <ResultCardPlugin key={idx} aiFeature={aiFeature} aiResult={selectedResult} />
          })
        : null}
    </div>
  )
}
